import React from 'react';
import './Card.scss';
import FeaturedPressCard from './types/FeaturedPressCard/FeaturedPressCard';
import RecentPressCard from './types/RecentPressCard/RecentPressCard';
import { CARD_TYPES } from '../../../constants/cardTypes';
import ArticleCard from './types/ArticleCard/ArticleCard';
import { getModuleBackgroundColor } from '../../../util/getModuleBackgroundColor';
import CaseStudyCard from './types/CaseStudyCard/CaseStudyCard';
import SolutionCard from './types/SolutionCard/SolutionCard';
import PressPreviewCard from './types/PressPreviewCard/PressPreviewCard';
import ToolCard from './types/ToolCard/ToolCard';
import FeaturedProductCard from './types/FeaturedProductCard/FeaturedProductCard';

/**
 * Renders a card
 * @param {array} tags - array of tags associated with card/post
 * @param {string} company - company name, pulled from within post
 * @param {string} type - the type of card content to render
 * @param {string} title - the card title
 * @param {string} excerpt - the card excerpt
 * @param {object} logo - the logo including shopify url and alt
 * @param {string} link - the link for the card
 * @param {string} date - the date for the card article
 * @param {boolean} isFeatured - is the card being featured or not
 * @param {boolean} isRecentNews - the card a recent news card or not
 * @param {string} backgroundColor
 * @param {boolean} isSearch
 * @param {Object} hoverImage - Image that appears when card is hovered
 * @param {String} quote - Quote text
 * @param {String} quoteAuthor - Quote Author
 * @param {array} logos - array of logos associated with press preview card
 * @param {string} industry
 * @param {string} starsAssetUrl
 * @param {object} product
 * @param {boolean} showRating
 * @param {boolean} showPrice
 * @param {boolean} isLinked
 * @param {boolean} enablePreorder
 * @returns {JSX.Element}
 * @constructor
 */
const Card = ({
  tags = null,
  company,
  type,
  title,
  excerpt,
  logo = null,
  link,
  date,
  isFeatured,
  isRecentNews,
  backgroundColor,
  isSearch = false,
  hoverImage,
  quote,
  quoteAuthor,
  logos = null,
  industry,
  starsAssetUrl,
  product,
  showRating,
  showPrice,
  isLinked,
  enablePreorder,
}) => {
  const getCardContent = (type) => {
    const {
      FEATURED_PRESS,
      RECENT_PRESS,
      ARTICLE,
      RECENT_NEWS,
      CASE_STUDY,
      SOLUTIONS,
      PRESS_PREVIEW,
      TOOL,
      FEATURED_TOOL,
      FEATURED_PRODUCT,
    } = CARD_TYPES;

    switch (true) {
      case type === FEATURED_PRESS:
        return <FeaturedPressCard logo={logo} title={title} link={link} />;
      case type === RECENT_PRESS:
        return (
          <RecentPressCard logo={logo} title={title} link={link} date={date} excerpt={excerpt} />
        );
      case type === ARTICLE:
      case type === RECENT_NEWS:
        return (
          <ArticleCard
            backgroundColor={backgroundColor}
            date={date}
            tags={tags}
            title={title}
            link={link}
            excerpt={excerpt}
            isFeatured={isFeatured}
            isRecentNews={isRecentNews}
            isSearch={isSearch}
          />
        );
      case type === CASE_STUDY:
        return (
          <CaseStudyCard
            backgroundColor={backgroundColor}
            date={date}
            company={company}
            tags={tags}
            title={title}
            link={link}
            excerpt={excerpt}
            isFeatured={isFeatured}
            isRecentNews={isRecentNews}
            isSearch={isSearch}
          />
        );
      case type === SOLUTIONS:
        return (
          <SolutionCard
            title={title}
            desc={excerpt}
            link={link}
            hoverImage={hoverImage}
            quote={quote}
            quoteAuthor={quoteAuthor}
          />
        );
      case type === PRESS_PREVIEW:
        return <PressPreviewCard logos={logos} />;
      case type === TOOL:
      case type === FEATURED_TOOL:
        return (
          <ToolCard
            industry={industry}
            title={title}
            excerpt={excerpt}
            link={link}
            isFeatured={isFeatured}
            backgroundColor={backgroundColor}
          />
        );
      case type === FEATURED_PRODUCT:
        return (
          <FeaturedProductCard
            product={product}
            starsAssetUrl={starsAssetUrl}
            showRating={showRating}
            showPrice={showPrice}
            isLinked={isLinked}
            enablePreorder={enablePreorder}
          />
        );
      // add more card types
    }
  };

  // Some cards need to have their entire containers clickable
  const CLICKABLE_TYPES = [
    CARD_TYPES.FEATURED_PRESS,
    CARD_TYPES.ARTICLE,
    CARD_TYPES.RECENT_NEWS,
    CARD_TYPES.CASE_STUDY,
    CARD_TYPES.TOOL,
    CARD_TYPES.FEATURED_TOOL,
  ];

  const anchorAttrs = {
    href: link,
    rel: type === CARD_TYPES.FEATURED_PRESS ? 'noopener noreferrer' : null,
    target: type === CARD_TYPES.FEATURED_PRESS ? '_blank' : '_self',
    className: 'c-card__link-overlay',
  };

  return (
    <div
      className={`c-card ${!isFeatured ? type : 'is-featured'} ${
        backgroundColor ? getModuleBackgroundColor(backgroundColor) : ''
      }`}
    >
      {CLICKABLE_TYPES.includes(type) && <a {...anchorAttrs}></a>}
      <div className={`c-card__inner ${type}`}>{getCardContent(type)}</div>
    </div>
  );
};

export default Card;
